import React from "react";
import { RefreshCw, X } from "lucide-react";
import { useVersion } from "../../context/VersionContext";
import { useTranslation } from "react-i18next";
import { cn } from "../../lib/utils";

interface UpdateBannerProps {
  className?: string;
}

export function UpdateBanner({ className }: UpdateBannerProps) {
  const { isUpdateAvailable, latestVersion, dismissUpdate, reload } = useVersion();
  const { t } = useTranslation();

  if (!isUpdateAvailable) {
    return null;
  }

  return (
    <div
      className={cn(
        "fixed top-0 left-0 right-0 z-[100] bg-primary text-primary-foreground",
        "px-4 py-2 shadow-lg",
        "animate-in slide-in-from-top duration-300",
        className
      )}
    >
      <div className="container mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <RefreshCw className="h-4 w-4 flex-shrink-0 animate-spin-slow" />
          <p className="text-sm font-medium truncate">
            {t("version.updateAvailable", "A new version is available")}
            {latestVersion?.version && (
              <span className="ml-1 opacity-75">
                (Version {latestVersion.version}{latestVersion.buildNumber ? ` (${latestVersion.buildNumber})` : ''})
              </span>
            )}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={reload}
            className={cn(
              "inline-flex items-center gap-1.5 px-3 py-1 rounded-lg",
              "bg-white/20 hover:bg-white/30 transition-colors",
              "text-sm font-medium"
            )}
          >
            <RefreshCw className="h-3.5 w-3.5" />
            {t("version.refreshNow", "Refresh Now")}
          </button>
          
          <button
            onClick={dismissUpdate}
            className={cn(
              "p-1 rounded-lg",
              "hover:bg-white/20 transition-colors"
            )}
            aria-label={t("common.close", "Close")}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default UpdateBanner;
